import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { CITIES_BY_STATE, OTHER_CITY, citiesFor } from "@/constants/cities";

/**
 * PIN-code auto-fill using India Post's free public API
 * (https://api.postalpincode.in/pincode/{pin}) — no key required.
 *
 * On a valid 6-digit PIN we resolve the PostOffice → State + District and
 * hand the caller a `{state, city, cityOther, district, area}` payload via
 * `onResolved`. The caller is responsible for merging into their form
 * state (so the user can still edit anything by hand).
 *
 * State strings from India Post use their own casing ("MAHARASHTRA",
 * "ANDAMAN AND NICOBAR ISLANDS", "NCT OF DELHI") — we normalise them to
 * the keys used across our app (STATES list / CITIES_BY_STATE).
 */

// India-Post-name → canonical app-name aliases (uppercase LHS).
const STATE_ALIASES = {
  "ANDAMAN AND NICOBAR ISLANDS": "Andaman & Nicobar",
  "NCT OF DELHI": "Delhi",
  "JAMMU AND KASHMIR": "Jammu & Kashmir",
  "DADRA AND NAGAR HAVELI": "Dadra & Nagar Haveli and Daman & Diu",
  "DAMAN AND DIU": "Dadra & Nagar Haveli and Daman & Diu",
  "PONDICHERRY": "Puducherry",
  "ORISSA": "Odisha",
};

// India Post still uses the pre-rename district labels for several major
// cities. Normalise them so the returned district matches a canonical
// entry in citiesFor(state) and the picker shows the modern name.
const DISTRICT_ALIASES = {
  "BANGALORE":         "Bengaluru",
  "BANGALORE URBAN":   "Bengaluru",
  "BANGALORE RURAL":   "Bengaluru",
  "BOMBAY":            "Mumbai",
  "MADRAS":            "Chennai",
  "CALCUTTA":          "Kolkata",
  "POONA":             "Pune",
  "COCHIN":            "Kochi",
  "TRIVANDRUM":        "Thiruvananthapuram",
  "MYSORE":            "Mysuru",
  "MANGALORE":         "Mangaluru",
  "BELGAUM":           "Belagavi",
  "GULBARGA":          "Kalaburagi",
  "BELLARY":           "Ballari",
  "TUMKUR":            "Tumakuru",
  "HUBLI":             "Hubballi",
  "SHIMOGA":           "Shivamogga",
  "CHIKMAGALUR":       "Chikkamagaluru",
  "BIJAPUR":           "Vijayapura",
  "GAUHATI":           "Guwahati",
  "GURGAON":           "Gurugram",
  "ALLAHABAD":         "Prayagraj",
  "FAIZABAD":          "Ayodhya",
  "BENARES":           "Varanasi",
};

const STATE_LIST = Object.keys(CITIES_BY_STATE);

function normalizeState(raw) {
  if (!raw) return "";
  const u = raw.trim().toUpperCase();
  if (STATE_ALIASES[u]) return STATE_ALIASES[u];
  const exact = STATE_LIST.find((s) => s.toUpperCase() === u);
  if (exact) return exact;
  // Try substituting "AND" → "&" (e.g. "JAMMU AND KASHMIR")
  const swapped = u.replace(/\bAND\b/g, "&").replace(/\s+/g, " ").trim();
  return STATE_LIST.find((s) => s.toUpperCase() === swapped) || raw;
}

function matchCity(state, district) {
  if (!state) return { city: OTHER_CITY, cityOther: district || "" };
  const raw = (district || "").trim();
  const aliased = DISTRICT_ALIASES[raw.toUpperCase()] || raw;
  const cities = citiesFor(state);
  const hit = cities.find((c) => c.toLowerCase() === aliased.toLowerCase());
  return hit ? { city: hit, cityOther: "" } : { city: OTHER_CITY, cityOther: aliased };
}

export default function PinCodeLookup({
  pin = "",
  onChange,
  onResolved,
  disabled = false,
  compact = false,
  testidPrefix = "pin",
}) {
  const [state, setState] = useState({ status: "idle", info: null });

  useEffect(() => {
    const p = (pin || "").trim();
    if (!p) { setState({ status: "idle", info: null }); return; }
    if (!/^\d{6}$/.test(p)) {
      setState({ status: "invalid", info: { error: "PIN must be 6 digits" } });
      return;
    }
    let cancelled = false;
    setState({ status: "loading", info: null });
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://api.postalpincode.in/pincode/${p}`);
        const data = await r.json();
        if (cancelled) return;
        const entry = Array.isArray(data) ? data[0] : null;
        if (!entry || entry.Status !== "Success" || !entry.PostOffice?.length) {
          setState({ status: "invalid", info: { error: entry?.Message || "PIN not found" } });
          return;
        }
        const po = entry.PostOffice[0];
        const stateName = normalizeState(po.State);
        const { city, cityOther } = matchCity(stateName, po.District);
        const resolved = {
          state: stateName,
          city,
          cityOther,
          district: po.District,
          area: po.Name,
        };
        setState({ status: "ok", info: { ...resolved, count: entry.PostOffice.length } });
        onResolved?.(resolved);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "invalid", info: { error: "Lookup failed — check your connection" } });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const inputCls = "mt-1 w-full border border-border px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition font-mono tracking-widest";
  const labelCls = compact
    ? "text-[10px] uppercase tracking-[0.3em] text-navy/60"
    : "text-[11px] uppercase tracking-widest text-navy/60";

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className={labelCls}>PIN Code · Auto-fill</label>
        {state.status === "loading" && <Loader2 size={12} className="text-gold animate-spin" />}
        {state.status === "ok"      && <CheckCircle2 size={12} className="text-olive" />}
        {state.status === "invalid" && <AlertCircle  size={12} className="text-rose" />}
      </div>
      <input
        data-testid={`${testidPrefix}-code`}
        value={pin || ""}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="e.g. 400001"
        inputMode="numeric"
        maxLength={6}
        disabled={disabled}
        className={`${inputCls} ${
          state.status === "ok"      ? "border-olive/60 focus:border-olive focus:ring-olive/20" :
          state.status === "invalid" ? "border-rose/60  focus:border-rose  focus:ring-rose/20"  : ""
        }`}
      />
      {state.status === "ok" && state.info && (
        <div data-testid={`${testidPrefix}-verified`} className="mt-1 text-[11px] text-olive leading-snug">
          ✓ {state.info.area} · {state.info.district}, {state.info.state}
          {state.info.count > 1 && <span className="text-navy/40"> · +{state.info.count - 1} more post office{state.info.count > 2 ? "s" : ""}</span>}
        </div>
      )}
      {state.status === "invalid" && state.info?.error && (
        <div data-testid={`${testidPrefix}-error`} className="mt-1 text-[11px] text-rose leading-snug">! {state.info.error}</div>
      )}
      {state.status === "idle" && (
        <div className="mt-1 text-[11px] text-navy/50 leading-snug">Type your 6-digit PIN — we'll fill state and city for you.</div>
      )}
    </div>
  );
}
